import { oc } from "@orpc/contract";
import { z } from "zod";
import { lenientEnum } from "../../schemas/common";

/**
 * AR Cash Receipts RPC Contract
 *
 * Provides endpoints for syncing Plaid bank transactions, listing Plaid bank
 * transactions in the review queue, fetching receipt detail with AI reasoning,
 * and overriding customer assignment.
 *
 * Note: CashReceiptStatusSchema is intentionally separate from PaymentStatusSchema
 * (see RESEARCH pitfall #6 — Plaid bank transactions only use needs_review / matched).
 */

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

/**
 * Status values applicable to Plaid bank transactions.
 * Separate from the full PaymentStatusSchema — bank transactions start at needs_review
 * and transition to matched on successful match to an open AR payment.
 */
export const CashReceiptStatusSchema = z.enum(["needs_review", "matched"]);

/**
 * AI confidence levels from AiReasoningAudit
 */
export const ConfidenceLevelSchema = z.enum(["high", "medium", "low", "none"]);

/**
 * Pagination schema for cash receipt list responses
 */
export const CashReceiptPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export const SyncCashReceiptsInputSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
});

const IngestionErrorSchema = z.object({
  transactionId: z.string(),
  amount: z.number(),
  date: z.string(),
  merchantName: z.string().nullable(),
  error: z.string(),
});

const ResolutionErrorSchema = z.object({
  bankTransactionId: z.string(),
  error: z.string(),
});

const MatchingErrorSchema = z.object({
  bankTransactionId: z.string(),
  error: z.string(),
});

export const SyncCashReceiptsResponseSchema = z.object({
  ingestion: z.object({
    created: z.int().nonnegative(),
    updated: z.int().nonnegative(),
    skipped: z.int().nonnegative(),
    errors: z.array(IngestionErrorSchema),
  }),
  resolution: z.object({
    resolved: z.int().nonnegative(),
    skipped: z.int().nonnegative(),
    errors: z.array(ResolutionErrorSchema),
  }),
  matching: z.object({
    matched: z.int().nonnegative(),
    unmatched: z.int().nonnegative(),
    errors: z.array(MatchingErrorSchema),
  }),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListCashReceiptsInputSchema = z.object({
  status: z.array(CashReceiptStatusSchema).optional(),
  paymentDateFrom: z.string().optional(),
  paymentDateTo: z.string().optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

export const CashReceiptListItemSchema = z.object({
  id: UUIDSchema,
  paymentDate: z.string().nullable(),
  paymentAmount: DecimalStringSchema.nullable(),
  customerName: z.string().nullable(),
  customerId: UUIDSchema.nullable(),
  status: lenientEnum(CashReceiptStatusSchema),
  bankDescription: z.string().nullable(),
  confidence: lenientEnum(ConfidenceLevelSchema).nullable(),
});

export const ListCashReceiptsResponseSchema = z.object({
  receipts: z.array(CashReceiptListItemSchema),
  pagination: CashReceiptPaginationSchema,
});

// ---------------------------------------------------------------------------
// Get (detail)
// ---------------------------------------------------------------------------

export const GetCashReceiptInputSchema = z.object({
  id: UUIDSchema,
});

export const CashReceiptLinkedPaymentSchema = z.object({
  id: UUIDSchema,
  referenceNumber: z.string().nullable(),
  paymentAmount: DecimalStringSchema.nullable(),
  paymentDate: z.string().nullable(),
  status: z.string(),
  originSystem: z.string().nullable(),
});

export const CashReceiptDetailSchema = CashReceiptListItemSchema.extend({
  /**
   * Source system. `"manual"` rows were entered through the UI and are the
   * only ones the manual-entry endpoints allow to be edited; Plaid/Famous rows
   * are read-only.
   */
  originSystem: z.string().nullable(),
  customerMatch: z.object({
    customerId: UUIDSchema.nullable(),
    customerName: z.string().nullable(),
    confidence: lenientEnum(ConfidenceLevelSchema).nullable(),
    reasoning: z.string().nullable(),
  }),
  linkedPayment: CashReceiptLinkedPaymentSchema.nullable(),
  bankTransaction: z
    .object({
      type: z.string().nullable(),
      accountId: z.string().nullable(),
      merchantName: z.string().nullable(),
      description: z.string().nullable(),
    })
    .nullable(),
});

export const GetCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Override customer
// ---------------------------------------------------------------------------

export const OverrideCustomerInputSchema = z.object({
  receiptId: UUIDSchema,
  customerId: UUIDSchema,
  triggerRematch: z.boolean().optional(),
});

export const OverrideCustomerResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const DeleteCashReceiptInputSchema = z.object({
  id: UUIDSchema,
});

export const DeleteCashReceiptResponseSchema = z.object({
  success: z.literal(true),
});

// ---------------------------------------------------------------------------
// Manual create / edit
// ---------------------------------------------------------------------------

export const CreateCashReceiptInputSchema = z.object({
  amount: DecimalStringSchema,
  transactionDate: z.string().nullable().optional(),
  merchantName: z.string().nullable().optional(),
  paymentChannel: z.string().nullable().optional(),
  customerId: UUIDSchema.nullable().optional(),
});

export const CreateCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
});

export const UpdateCashReceiptInputSchema = z.object({
  id: UUIDSchema,
  amount: DecimalStringSchema.nullable().optional(),
  transactionDate: z.string().nullable().optional(),
  merchantName: z.string().nullable().optional(),
  paymentChannel: z.string().nullable().optional(),
});

export const UpdateCashReceiptResponseSchema = z.object({
  receipt: CashReceiptDetailSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/**
 * AR Cash Receipts RPC contract:
 * - sync (POST): trigger Plaid sync + pipeline for a date range
 * - list (GET): paginated review queue with optional status/date filters
 * - get (GET): enriched single receipt with AI reasoning and linked payment
 * - overrideCustomer (POST): update customer assignment and attempt re-match
 * - delete (POST): permanently remove a cash receipt (operator escape hatch
 *   for badly-extracted rows; unlinks any associated Payment first)
 */
export const arCashReceiptsContracts = {
  sync: oc
    .input(SyncCashReceiptsInputSchema)
    .output(SyncCashReceiptsResponseSchema)
    .route({ method: "POST" }),
  list: oc
    .input(ListCashReceiptsInputSchema)
    .output(ListCashReceiptsResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetCashReceiptInputSchema)
    .output(GetCashReceiptResponseSchema)
    .route({ method: "GET" }),
  overrideCustomer: oc
    .input(OverrideCustomerInputSchema)
    .output(OverrideCustomerResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteCashReceiptInputSchema)
    .output(DeleteCashReceiptResponseSchema)
    .route({ method: "POST" }),
  create: oc
    .input(CreateCashReceiptInputSchema)
    .output(CreateCashReceiptResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateCashReceiptInputSchema)
    .output(UpdateCashReceiptResponseSchema)
    .route({ method: "POST" }),
} as const;

// Type exports
export type CashReceiptStatus = z.infer<typeof CashReceiptStatusSchema>;
export type ConfidenceLevel = z.infer<typeof ConfidenceLevelSchema>;
export type CashReceiptPagination = z.infer<typeof CashReceiptPaginationSchema>;
export type SyncCashReceiptsInput = z.infer<typeof SyncCashReceiptsInputSchema>;
export type SyncCashReceiptsResponse = z.infer<
  typeof SyncCashReceiptsResponseSchema
>;
export type ListCashReceiptsInput = z.infer<typeof ListCashReceiptsInputSchema>;
export type ListCashReceiptsResponse = z.infer<
  typeof ListCashReceiptsResponseSchema
>;
export type CashReceiptListItem = z.infer<typeof CashReceiptListItemSchema>;
export type GetCashReceiptInput = z.infer<typeof GetCashReceiptInputSchema>;
export type GetCashReceiptResponse = z.infer<
  typeof GetCashReceiptResponseSchema
>;
export type CashReceiptDetail = z.infer<typeof CashReceiptDetailSchema>;
export type CashReceiptLinkedPayment = z.infer<
  typeof CashReceiptLinkedPaymentSchema
>;
export type OverrideCustomerInput = z.infer<typeof OverrideCustomerInputSchema>;
export type OverrideCustomerResponse = z.infer<
  typeof OverrideCustomerResponseSchema
>;
export type DeleteCashReceiptInput = z.infer<
  typeof DeleteCashReceiptInputSchema
>;
export type DeleteCashReceiptResponse = z.infer<
  typeof DeleteCashReceiptResponseSchema
>;
export type CreateCashReceiptInput = z.infer<
  typeof CreateCashReceiptInputSchema
>;
export type CreateCashReceiptResponse = z.infer<
  typeof CreateCashReceiptResponseSchema
>;
export type UpdateCashReceiptInput = z.infer<
  typeof UpdateCashReceiptInputSchema
>;
export type UpdateCashReceiptResponse = z.infer<
  typeof UpdateCashReceiptResponseSchema
>;
